import { useState, useEffect, useCallback } from "react";
import { AdminLayout } from "@/components/shared/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  getInventoryCategories, createInventoryCategory, updateInventoryCategory, deleteInventoryCategory,
  getInventoryVendors, createInventoryVendor, updateInventoryVendor, deleteInventoryVendor,
  getInventoryProducts, createInventoryProduct, updateInventoryProduct, deleteInventoryProduct,
  getPurchaseOrders, createPurchaseOrder, updatePurchaseOrder, deletePurchaseOrder,
  receiveOrder, getReceiveHistory,
  getInventoryCorrections, createInventoryCorrection,
  getInventoryStock, searchSerialNumbers, getProductSerials,
  getInventoryConditions, createInventoryCondition, updateInventoryCondition, deleteInventoryCondition,
  getCarrierLocks, createCarrierLock, updateCarrierLock, deleteCarrierLock,
  clearAllInventory, uploadFile,
  getInventorySubcategories, createInventorySubcategory, updateInventorySubcategory, deleteInventorySubcategory,
  getInventoryItemGroups, createInventoryItemGroup, updateInventoryItemGroup, deleteInventoryItemGroup,
  getInventoryCompatibleModels, createInventoryCompatibleModel, updateInventoryCompatibleModel, deleteInventoryCompatibleModel,
  getInventoryBrands, createInventoryBrand, updateInventoryBrand, deleteInventoryBrand,
  getInventoryVariants, createInventoryVariant, updateInventoryVariant, deleteInventoryVariant,
  generateProductSku, generateProductUpc,
  getStockViewer, cancelMarketplaceOrder,
  deleteReceiveHistory, deleteInventoryCorrection,
} from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Package, Plus, Edit, Trash2, Loader2, Search, Tag, Truck, ClipboardList,
  BarChart3, Hash, Layers, ArrowDown, ArrowUp, Check, X, Shield, Smartphone, ShoppingCart,
  AlertTriangle, Upload, Image as ImageIcon, Wand2, FolderTree, Cpu, Boxes, ChevronDown, Copy, Award, Palette,
} from "lucide-react";
import AdminMarketplaceOrders from "./AdminMarketplaceOrders";

const TABS = [
  { key: "products", label: "Products", icon: Package },
  { key: "categories", label: "Categories", icon: Tag },
  { key: "conditions", label: "Conditions", icon: Shield },
  { key: "carrier_locks", label: "Carrier Locks", icon: Smartphone },
  { key: "vendors", label: "Vendors", icon: Truck },
  { key: "purchase_orders", label: "Purchase Orders", icon: ClipboardList },
  { key: "receive", label: "Receive", icon: ArrowDown },
  { key: "history", label: "History", icon: BarChart3 },
  { key: "corrections", label: "Corrections", icon: ArrowUp },
  { key: "stock", label: "Stock Viewer", icon: Layers },
  { key: "serials", label: "Serials", icon: Hash },
  { key: "orders", label: "Orders", icon: ShoppingCart },
];

// ========== CATEGORIES TAB ==========
// ========== CATEGORIES PANEL (Categories | Sub-Categories | Compatible Models) ==========
const CATEGORY_SUBTABS = [
  { key: "categories", label: "Categories", icon: Tag },
  { key: "subcategories", label: "Sub-Categories", icon: FolderTree },
  { key: "item_groups", label: "Item Groups", icon: Boxes },
  { key: "brands", label: "Brands", icon: Award },
  { key: "variants", label: "Variants", icon: Palette },
  { key: "compatible_models", label: "Compatible Models", icon: Cpu },
];

function CategoriesPanel() {
  const [sub, setSub] = useState("categories");
  return (
    <div data-testid="categories-panel">
      <div className="flex flex-wrap gap-1 bg-slate-100 rounded-lg p-1 mb-5 w-fit" data-testid="cat-subtabs">
        {CATEGORY_SUBTABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setSub(t.key)}
              data-testid={`cat-subtab-${t.key}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                sub === t.key ? "bg-white text-teal shadow-sm" : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </div>
      {sub === "categories" && <CategoriesTab />}
      {sub === "subcategories" && <SubCategoriesTab />}
      {sub === "item_groups" && <ItemGroupsTab />}
      {sub === "brands" && <BrandsTab />}
      {sub === "variants" && <VariantsTab />}
      {sub === "compatible_models" && <CompatibleModelsTab />}
    </div>
  );
}

// ========== BRANDS TAB ==========
function BrandsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "" });
  const [submitting, setSubmitting] = useState(false);

  const fetchAll = useCallback(async () => {
    try { setItems(await getInventoryBrands()); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openDialog = (item = null) => {
    setEditing(item);
    setForm(item ? { name: item.name, description: item.description || "" } : { name: "", description: "" });
    setDialog(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setSubmitting(true);
    try {
      if (editing) { await updateInventoryBrand(editing.id, form); toast.success("Brand updated"); }
      else { await createInventoryBrand(form); toast.success("Brand created"); }
      setDialog(false); fetchAll();
    } catch (e) { toast.error("Error saving brand"); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this brand?")) return;
    try { await deleteInventoryBrand(id); toast.success("Deleted"); fetchAll(); }
    catch (e) { toast.error("Error"); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-teal" /></div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-slate-500">{items.length} brands</p>
        <Button onClick={() => openDialog()} size="sm" className="bg-teal hover:bg-teal/90" data-testid="add-brand-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Brand
        </Button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((b) => (
          <Card key={b.id} className="border-slate-200">
            <CardContent className="flex items-center justify-between py-3 px-4">
              <div>
                <p className="font-medium text-navy" data-testid={`brand-name-${b.id}`}>{b.name}</p>
                {b.description && <p className="text-xs text-slate-500">{b.description}</p>}
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" onClick={() => openDialog(b)} data-testid={`edit-brand-${b.id}`}><Edit className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(b.id)} className="text-red-500" data-testid={`del-brand-${b.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && <p className="col-span-full text-center text-slate-400 py-8">No brands yet. Add brands like "Apple", "Samsung", "Anker", etc.</p>}
      </div>
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} Brand</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Apple, Samsung, Anker..." data-testid="brand-name-input" /></div>
            <div><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="brand-desc-input" /></div>
            <Button onClick={handleSave} disabled={submitting} className="w-full bg-teal hover:bg-teal/90" data-testid="save-brand-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ========== VARIANTS TAB (color / style values) ==========
function VariantsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", color_hex: "#000000" });
  const [submitting, setSubmitting] = useState(false);

  const fetchAll = useCallback(async () => {
    try { setItems(await getInventoryVariants()); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openDialog = (item = null) => {
    setEditing(item);
    setForm(item ? { name: item.name, color_hex: item.color_hex || "#000000" } : { name: "", color_hex: "#000000" });
    setDialog(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setSubmitting(true);
    try {
      if (editing) { await updateInventoryVariant(editing.id, form); toast.success("Variant updated"); }
      else { await createInventoryVariant(form); toast.success("Variant created"); }
      setDialog(false); fetchAll();
    } catch (e) { toast.error("Error saving variant"); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this variant? Products using it will keep their reference but show no swatch.")) return;
    try { await deleteInventoryVariant(id); toast.success("Deleted"); fetchAll(); }
    catch (e) { toast.error("Error"); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-teal" /></div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-slate-500">{items.length} variants</p>
        <Button onClick={() => openDialog()} size="sm" className="bg-teal hover:bg-teal/90" data-testid="add-variant-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Variant
        </Button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {items.map((v) => (
          <Card key={v.id} className="border-slate-200">
            <CardContent className="flex items-center justify-between py-3 px-4">
              <div className="flex items-center gap-3">
                <span
                  className="w-7 h-7 rounded-full border border-slate-200 shadow-sm"
                  style={{ backgroundColor: v.color_hex || "#cbd5e1" }}
                  data-testid={`variant-swatch-${v.id}`}
                />
                <div>
                  <p className="font-medium text-navy" data-testid={`variant-name-${v.id}`}>{v.name}</p>
                  <p className="text-xs text-slate-400 font-mono">{v.color_hex || "—"}</p>
                </div>
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" onClick={() => openDialog(v)} data-testid={`edit-variant-${v.id}`}><Edit className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(v.id)} className="text-red-500" data-testid={`del-variant-${v.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && <p className="col-span-full text-center text-slate-400 py-8">No variants yet. Add color or style values like "Black", "Blue", "Silver" — these appear as swatches on the dealer marketplace.</p>}
      </div>
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} Variant</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Black, Blue, Silver, Rose Gold..." data-testid="variant-name-input" /></div>
            <div>
              <Label>Color</Label>
              <p className="text-xs text-slate-500 mb-2">Used as the swatch color shown to dealers next to the price.</p>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={form.color_hex || "#000000"}
                  onChange={(e) => setForm({ ...form, color_hex: e.target.value })}
                  className="w-14 h-10 rounded border border-slate-200 cursor-pointer"
                  data-testid="variant-color-picker"
                />
                <Input
                  value={form.color_hex}
                  onChange={(e) => setForm({ ...form, color_hex: e.target.value })}
                  placeholder="#000000"
                  className="font-mono"
                  data-testid="variant-color-input"
                />
              </div>
            </div>
            <Button onClick={handleSave} disabled={submitting} className="w-full bg-teal hover:bg-teal/90" data-testid="save-variant-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ========== SUB-CATEGORIES TAB ==========
function SubCategoriesTab() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", category_id: "", description: "" });
  const [submitting, setSubmitting] = useState(false);
  const [filterCat, setFilterCat] = useState("");

  const fetchAll = useCallback(async () => {
    try {
      const [subs, cats] = await Promise.all([
        getInventorySubcategories(),
        getInventoryCategories(),
      ]);
      setItems(subs);
      setCategories(cats);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openDialog = (item = null) => {
    setEditing(item);
    setForm(item
      ? { name: item.name, category_id: item.category_id || "", description: item.description || "" }
      : { name: "", category_id: filterCat || "", description: "" });
    setDialog(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (!form.category_id) return toast.error("Parent category is required");
    setSubmitting(true);
    try {
      if (editing) { await updateInventorySubcategory(editing.id, form); toast.success("Sub-category updated"); }
      else { await createInventorySubcategory(form); toast.success("Sub-category created"); }
      setDialog(false); fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error saving"); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this sub-category?")) return;
    try { await deleteInventorySubcategory(id); toast.success("Deleted"); fetchAll(); }
    catch (e) { toast.error("Error"); }
  };

  const catName = (id) => categories.find((c) => c.id === id)?.name || "—";
  const filtered = filterCat ? items.filter((i) => i.category_id === filterCat) : items;

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-teal" /></div>;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <p className="text-sm text-slate-500">{filtered.length} sub-categories</p>
          <select
            value={filterCat}
            onChange={(e) => setFilterCat(e.target.value)}
            className="h-8 rounded-md border border-slate-200 bg-white px-2 text-sm"
            data-testid="subcat-filter"
          >
            <option value="">All categories</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <Button onClick={() => openDialog()} size="sm" className="bg-teal hover:bg-teal/90" data-testid="add-subcat-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Sub-Category
        </Button>
      </div>
      <div className="grid gap-3">
        {filtered.map((s) => (
          <Card key={s.id} className="border-slate-200">
            <CardContent className="flex items-center justify-between py-3 px-4">
              <div>
                <p className="font-medium text-navy" data-testid={`subcat-name-${s.id}`}>{s.name}</p>
                <p className="text-xs text-slate-500">
                  Parent: <span className="text-teal">{catName(s.category_id)}</span>
                  {s.description ? ` • ${s.description}` : ""}
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" size="icon" onClick={() => openDialog(s)} data-testid={`edit-subcat-${s.id}`}><Edit className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(s.id)} className="text-red-500" data-testid={`del-subcat-${s.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {filtered.length === 0 && <p className="text-center text-slate-400 py-8">No sub-categories yet</p>}
      </div>
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} Sub-Category</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Parent Category *</Label>
              <select
                value={form.category_id}
                onChange={(e) => setForm({ ...form, category_id: e.target.value })}
                className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                data-testid="subcat-parent-select"
              >
                <option value="">Select category...</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="subcat-name-input" /></div>
            <div><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="subcat-desc-input" /></div>
            <Button onClick={handleSave} disabled={submitting} className="w-full bg-teal hover:bg-teal/90" data-testid="save-subcat-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ========== ITEM GROUPS TAB ==========
function ItemGroupsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "" });
  const [submitting, setSubmitting] = useState(false);

  const fetchAll = useCallback(async () => {
    try { setItems(await getInventoryItemGroups()); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openDialog = (item = null) => {
    setEditing(item);
    setForm(item ? { name: item.name, description: item.description || "" } : { name: "", description: "" });
    setDialog(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setSubmitting(true);
    try {
      if (editing) { await updateInventoryItemGroup(editing.id, form); toast.success("Item group updated"); }
      else { await createInventoryItemGroup(form); toast.success("Item group created"); }
      setDialog(false); fetchAll();
    } catch (e) { toast.error("Error saving"); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this item group?")) return;
    try { await deleteInventoryItemGroup(id); toast.success("Deleted"); fetchAll(); }
    catch (e) { toast.error("Error"); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-teal" /></div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-slate-500">{items.length} item groups</p>
        <Button onClick={() => openDialog()} size="sm" className="bg-teal hover:bg-teal/90" data-testid="add-itemgroup-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Item Group
        </Button>
      </div>
      <div className="grid gap-3">
        {items.map((g) => (
          <Card key={g.id} className="border-slate-200">
            <CardContent className="flex items-center justify-between py-3 px-4">
              <div>
                <p className="font-medium text-navy" data-testid={`itemgroup-name-${g.id}`}>{g.name}</p>
                {g.description && <p className="text-xs text-slate-500">{g.description}</p>}
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" size="icon" onClick={() => openDialog(g)} data-testid={`edit-itemgroup-${g.id}`}><Edit className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(g.id)} className="text-red-500" data-testid={`del-itemgroup-${g.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && <p className="text-center text-slate-400 py-8">No item groups yet</p>}
      </div>
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} Item Group</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Phone Cases, Smartwatches, Chargers" data-testid="itemgroup-name-input" /></div>
            <div><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="itemgroup-desc-input" /></div>
            <Button onClick={handleSave} disabled={submitting} className="w-full bg-teal hover:bg-teal/90" data-testid="save-itemgroup-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ========== COMPATIBLE MODELS TAB ==========
function CompatibleModelsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", brand: "" });
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState("");

  const fetchAll = useCallback(async () => {
    try { setItems(await getInventoryCompatibleModels()); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openDialog = (item = null) => {
    setEditing(item);
    setForm(item ? { name: item.name, brand: item.brand || "" } : { name: "", brand: "" });
    setDialog(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setSubmitting(true);
    try {
      if (editing) { await updateInventoryCompatibleModel(editing.id, form); toast.success("Model updated"); }
      else { await createInventoryCompatibleModel(form); toast.success("Model created"); }
      setDialog(false); fetchAll();
    } catch (e) { toast.error("Error saving"); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this compatible model?")) return;
    try { await deleteInventoryCompatibleModel(id); toast.success("Deleted"); fetchAll(); }
    catch (e) { toast.error("Error"); }
  };

  const filtered = items.filter((m) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return m.name?.toLowerCase().includes(q) || m.brand?.toLowerCase().includes(q);
  });

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-teal" /></div>;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2 flex-1 max-w-xs">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className="pl-9 h-8 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
              data-testid="compat-search"
            />
          </div>
        </div>
        <p className="text-sm text-slate-500">{filtered.length} models</p>
        <Button onClick={() => openDialog()} size="sm" className="bg-teal hover:bg-teal/90" data-testid="add-compat-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Model
        </Button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map((m) => (
          <Card key={m.id} className="border-slate-200">
            <CardContent className="flex items-center justify-between py-3 px-4">
              <div>
                <p className="font-medium text-navy" data-testid={`compat-name-${m.id}`}>{m.name}</p>
                {m.brand && <p className="text-xs text-slate-500">{m.brand}</p>}
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" onClick={() => openDialog(m)} data-testid={`edit-compat-${m.id}`}><Edit className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(m.id)} className="text-red-500" data-testid={`del-compat-${m.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {filtered.length === 0 && <p className="text-center text-slate-400 py-8 col-span-full">No compatible models yet</p>}
      </div>
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} Compatible Model</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Model Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="iPhone 15 Pro Max" data-testid="compat-name-input" /></div>
            <div><Label>Brand</Label><Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} placeholder="Apple, Samsung..." data-testid="compat-brand-input" /></div>
            <Button onClick={handleSave} disabled={submitting} className="w-full bg-teal hover:bg-teal/90" data-testid="save-compat-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ========== CATEGORIES TAB ==========
function CategoriesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "" });
  const [submitting, setSubmitting] = useState(false);

  const fetch = useCallback(async () => {
    try { setItems(await getInventoryCategories()); } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const openDialog = (item = null) => {
    setEditing(item);
    setForm(item ? { name: item.name, description: item.description || "" } : { name: "", description: "" });
    setDialog(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setSubmitting(true);
    try {
      if (editing) { await updateInventoryCategory(editing.id, form); toast.success("Category updated"); }
      else { await createInventoryCategory(form); toast.success("Category created"); }
      setDialog(false); fetch();
    } catch (e) { toast.error("Error saving category"); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    try { await deleteInventoryCategory(id); toast.success("Deleted"); fetch(); } catch (e) { toast.error("Error"); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-teal" /></div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-slate-500">{items.length} categories</p>
        <Button onClick={() => openDialog()} size="sm" className="bg-teal hover:bg-teal/90" data-testid="add-category-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Category
        </Button>
      </div>
      <div className="grid gap-3">
        {items.map((c) => (
          <Card key={c.id} className="border-slate-200">
            <CardContent className="flex items-center justify-between py-3 px-4">
              <div>
                <p className="font-medium text-navy" data-testid={`cat-name-${c.id}`}>{c.name}</p>
                {c.description && <p className="text-xs text-slate-500">{c.description}</p>}
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" size="icon" onClick={() => openDialog(c)} data-testid={`edit-cat-${c.id}`}><Edit className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(c.id)} className="text-red-500" data-testid={`del-cat-${c.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && <p className="text-center text-slate-400 py-8">No categories yet</p>}
      </div>
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} Category</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cat-name-input" /></div>
            <div><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="cat-desc-input" /></div>
            <Button onClick={handleSave} disabled={submitting} className="w-full bg-teal hover:bg-teal/90" data-testid="save-cat-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ========== CONDITIONS TAB ==========
function ConditionsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "" });
  const [submitting, setSubmitting] = useState(false);

  const fetch = useCallback(async () => {
    try { setItems(await getInventoryConditions()); } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const openDialog = (item = null) => {
    setEditing(item);
    setForm(item ? { name: item.name } : { name: "" });
    setDialog(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setSubmitting(true);
    try {
      if (editing) { await updateInventoryCondition(editing.id, form); toast.success("Condition updated"); }
      else { await createInventoryCondition(form); toast.success("Condition created"); }
      setDialog(false); fetch();
    } catch (e) { toast.error("Error saving condition"); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this condition?")) return;
    try { await deleteInventoryCondition(id); toast.success("Deleted"); fetch(); } catch (e) { toast.error("Error"); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-teal" /></div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-slate-500">{items.length} conditions</p>
        <Button onClick={() => openDialog()} size="sm" className="bg-teal hover:bg-teal/90" data-testid="add-condition-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Condition
        </Button>
      </div>
      <div className="grid gap-3">
        {items.map((c) => (
          <Card key={c.id} className="border-slate-200">
            <CardContent className="flex items-center justify-between py-3 px-4">
              <p className="font-medium text-navy" data-testid={`cond-name-${c.id}`}>{c.name}</p>
              <div className="flex gap-2">
                <Button variant="ghost" size="icon" onClick={() => openDialog(c)} data-testid={`edit-cond-${c.id}`}><Edit className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(c.id)} className="text-red-500" data-testid={`del-cond-${c.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && <p className="text-center text-slate-400 py-8">No conditions yet. Add conditions like "New", "Open Box", "Refurbished", etc.</p>}
      </div>
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} Condition</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cond-name-input" /></div>
            <Button onClick={handleSave} disabled={submitting} className="w-full bg-teal hover:bg-teal/90" data-testid="save-cond-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ========== CARRIER LOCKS TAB ==========
function CarrierLocksTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "" });
  const [submitting, setSubmitting] = useState(false);

  const fetch = useCallback(async () => {
    try { setItems(await getCarrierLocks()); } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const openDialog = (item = null) => {
    setEditing(item);
    setForm(item ? { name: item.name } : { name: "" });
    setDialog(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setSubmitting(true);
    try {
      if (editing) { await updateCarrierLock(editing.id, form); toast.success("Carrier lock updated"); }
      else { await createCarrierLock(form); toast.success("Carrier lock created"); }
      setDialog(false); fetch();
    } catch (e) { toast.error("Error saving carrier lock"); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this carrier lock?")) return;
    try { await deleteCarrierLock(id); toast.success("Deleted"); fetch(); } catch (e) { toast.error("Error"); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-teal" /></div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-slate-500">{items.length} carrier locks</p>
        <Button onClick={() => openDialog()} size="sm" className="bg-teal hover:bg-teal/90" data-testid="add-carrier-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Carrier Lock
        </Button>
      </div>
      <div className="grid gap-3">
        {items.map((c) => (
          <Card key={c.id} className="border-slate-200">
            <CardContent className="flex items-center justify-between py-3 px-4">
              <p className="font-medium text-navy" data-testid={`carrier-name-${c.id}`}>{c.name}</p>
              <div className="flex gap-2">
                <Button variant="ghost" size="icon" onClick={() => openDialog(c)} data-testid={`edit-carrier-${c.id}`}><Edit className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(c.id)} className="text-red-500" data-testid={`del-carrier-${c.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && <p className="text-center text-slate-400 py-8">No carrier locks yet. Add options like "Total Wireless", "Unlocked", etc.</p>}
      </div>
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} Carrier Lock</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="carrier-name-input" /></div>
            <Button onClick={handleSave} disabled={submitting} className="w-full bg-teal hover:bg-teal/90" data-testid="save-carrier-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ========== VENDORS TAB ==========
function VendorsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", contact_name: "", email: "", phone: "", address: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  const fetch = useCallback(async () => {
    try { setItems(await getInventoryVendors()); } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const openDialog = (item = null) => {
    setEditing(item);
    setForm(item ? { name: item.name, contact_name: item.contact_name || "", email: item.email || "", phone: item.phone || "", address: item.address || "", notes: item.notes || "" }
      : { name: "", contact_name: "", email: "", phone: "", address: "", notes: "" });
    setDialog(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setSubmitting(true);
    try {
      if (editing) { await updateInventoryVendor(editing.id, form); toast.success("Vendor updated"); }
      else { await createInventoryVendor(form); toast.success("Vendor created"); }
      setDialog(false); fetch();
    } catch (e) { toast.error("Error saving vendor"); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this vendor?")) return;
    try { await deleteInventoryVendor(id); toast.success("Deleted"); fetch(); } catch (e) { toast.error("Error"); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-teal" /></div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-slate-500">{items.length} vendors</p>
        <Button onClick={() => openDialog()} size="sm" className="bg-teal hover:bg-teal/90" data-testid="add-vendor-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Vendor
        </Button>
      </div>
      <div className="grid gap-3">
        {items.map((v) => (
          <Card key={v.id} className="border-slate-200">
            <CardContent className="flex items-center justify-between py-3 px-4">
              <div>
                <p className="font-medium text-navy">{v.name}</p>
                <p className="text-xs text-slate-500">{[v.contact_name, v.email, v.phone].filter(Boolean).join(" | ")}</p>
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" size="icon" onClick={() => openDialog(v)}><Edit className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(v.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && <p className="text-center text-slate-400 py-8">No vendors yet</p>}
      </div>
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} Vendor</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="vendor-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Contact</Label><Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} /></div>
              <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            </div>
            <div><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <Button onClick={handleSave} disabled={submitting} className="w-full bg-teal hover:bg-teal/90" data-testid="save-vendor-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ========== PRODUCTS TAB ==========
function ProductsTab() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [itemGroups, setItemGroups] = useState([]);
  const [compatibleModels, setCompatibleModels] = useState([]);
  const [brands, setBrands] = useState([]);
  const [variants, setVariants] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [carrierLocks, setCarrierLocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", sku: "", upc: "", category_id: "", subcategory_id: "", item_group_id: "", brand_id: "", variant_id: "", is_variant_of_product_id: "", variant_group_id: "", compatible_model_ids: [], warranty_period: "", cost_price: "", condition: "", retail_price: "", carrier_lock: "", description: "", image_url: "", track_serials: false });
  const [isVariantToggle, setIsVariantToggle] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [generatingSku, setGeneratingSku] = useState(false);
  const [generatingUpc, setGeneratingUpc] = useState(false);
  const [compatSearch, setCompatSearch] = useState("");
  const [compatOpen, setCompatOpen] = useState(false);

  const fetch = useCallback(async () => {
    try {
      const [prods, cats, subs, groups, models, brnds, vrnts, vends, conds, locks] = await Promise.all([
        getInventoryProducts(), getInventoryCategories(), getInventorySubcategories(),
        getInventoryItemGroups(),
        getInventoryCompatibleModels(),
        getInventoryBrands(),
        getInventoryVariants(),
        getInventoryVendors(), getInventoryConditions(), getCarrierLocks(),
      ]);
      setItems(prods); setCategories(cats); setSubcategories(subs);
      setItemGroups(groups);
      setCompatibleModels(models);
      setBrands(brnds);
      setVariants(vrnts);
      setVendors(vends); setConditions(conds); setCarrierLocks(locks);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const catMap = Object.fromEntries(categories.map((c) => [c.id, c.name]));
  const vendMap = Object.fromEntries(vendors.map((v) => [v.id, v.name]));
  const subcatMap = Object.fromEntries(subcategories.map((s) => [s.id, s.name]));
  const brandMap = Object.fromEntries(brands.map((b) => [b.id, b.name]));
  const variantMap = Object.fromEntries(variants.map((v) => [v.id, v]));
  const filteredSubcats = form.category_id
    ? subcategories.filter((s) => s.category_id === form.category_id)
    : [];
  const filteredCompatModels = compatibleModels.filter((m) => {
    if (!compatSearch.trim()) return true;
    const q = compatSearch.trim().toLowerCase();
    return (m.name || "").toLowerCase().includes(q) || (m.brand || "").toLowerCase().includes(q);
  });

  const openDialog = (item = null) => {
    setEditing(item);
    setForm(item ? { name: item.name, sku: item.sku || "", upc: item.upc || "", category_id: item.category_id || "", subcategory_id: item.subcategory_id || "", item_group_id: item.item_group_id || "", brand_id: item.brand_id || "", variant_id: item.variant_id || "", is_variant_of_product_id: "", variant_group_id: item.variant_group_id || "", compatible_model_ids: item.compatible_model_ids || [], warranty_period: item.warranty_period || "", cost_price: item.cost_price?.toString() || "", condition: item.condition || "", retail_price: item.retail_price?.toString() || "", carrier_lock: item.carrier_lock || "", description: item.description || "", image_url: item.image_url || "", track_serials: item.track_serials || false }
      : { name: "", sku: "", upc: "", category_id: "", subcategory_id: "", item_group_id: "", brand_id: "", variant_id: "", is_variant_of_product_id: "", variant_group_id: "", compatible_model_ids: [], warranty_period: "", cost_price: "", condition: "", retail_price: "", carrier_lock: "", description: "", image_url: "", track_serials: false });
    // Pre-toggle if editing an existing variant
    setIsVariantToggle(!!(item && (item.variant_id || item.variant_group_id)));
    setCompatSearch("");
    setCompatOpen(false);
    setDialog(true);
  };

  const duplicateProduct = (item) => {
    // Open a fresh "create" dialog pre-filled with this product's data,
    // but cleared id / sku / upc to avoid uniqueness collisions.
    setEditing(null);
    setForm({
      name: item.name ? `${item.name} (Copy)` : "",
      sku: "",
      upc: "",
      category_id: item.category_id || "",
      subcategory_id: item.subcategory_id || "",
      item_group_id: item.item_group_id || "",
      brand_id: item.brand_id || "",
      variant_id: "",
      is_variant_of_product_id: "",
      variant_group_id: "",
      compatible_model_ids: item.compatible_model_ids || [],
      warranty_period: item.warranty_period || "",
      cost_price: item.cost_price?.toString() || "",
      condition: item.condition || "",
      retail_price: item.retail_price?.toString() || "",
      carrier_lock: item.carrier_lock || "",
      description: item.description || "",
      image_url: item.image_url || "",
      track_serials: item.track_serials || false,
    });
    setIsVariantToggle(false);
    setCompatSearch("");
    setCompatOpen(false);
    setDialog(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      return toast.error("Please select an image file");
    }
    if (file.size > 5 * 1024 * 1024) {
      return toast.error("Image must be under 5MB");
    }
    setUploadingImage(true);
    try {
      const result = await uploadFile(file, "products");
      setForm((f) => ({ ...f, image_url: result.url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploadingImage(false);
      e.target.value = "";
    }
  };

  const handleGenerateSku = async () => {
    setGeneratingSku(true);
    try {
      const res = await generateProductSku();
      setForm((f) => ({ ...f, sku: res.sku }));
    } catch (e) { toast.error("Failed to generate SKU"); }
    finally { setGeneratingSku(false); }
  };

  const handleGenerateUpc = async () => {
    setGeneratingUpc(true);
    try {
      const res = await generateProductUpc();
      setForm((f) => ({ ...f, upc: res.upc }));
    } catch (e) { toast.error("Failed to generate UPC"); }
    finally { setGeneratingUpc(false); }
  };

  const toggleCompatibleModel = (modelId) => {
    setForm((f) => {
      const current = f.compatible_model_ids || [];
      const next = current.includes(modelId)
        ? current.filter((id) => id !== modelId)
        : [...current, modelId];
      return { ...f, compatible_model_ids: next };
    });
  };

  const handleSave = async () => {
    // Required-field validation (per user spec)
    if (!form.name.trim()) return toast.error("Name is required");
    const sku = form.sku?.trim();
    const upc = form.upc?.trim();
    if (!sku) return toast.error("SKU is required");
    if (!upc) return toast.error("UPC is required");
    if (!form.category_id) return toast.error("Category is required");
    if (!form.subcategory_id) {
      if (filteredSubcats.length === 0) {
        return toast.error("This category has no sub-categories yet — add one first under Categories → Sub-Categories");
      }
      return toast.error("Sub-Category is required");
    }
    if (!form.item_group_id) {
      if (itemGroups.length === 0) {
        return toast.error("Add at least one Item Group first under Categories → Item Groups");
      }
      return toast.error("Item Group is required");
    }
    if (form.cost_price === "" || form.cost_price === null || isNaN(parseFloat(form.cost_price))) {
      return toast.error("Cost Price is required");
    }
    if (!form.condition) return toast.error("Condition is required");
    if (form.retail_price === "" || form.retail_price === null || isNaN(parseFloat(form.retail_price))) {
      return toast.error("Retail Price is required");
    }
    // Frontend duplicate-check (server still enforces)
    const dupSku = items.find((p) => p.sku === sku && p.id !== editing?.id);
    if (dupSku) return toast.error(`SKU "${sku}" is already used by "${dupSku.name}".`);
    const dupUpc = items.find((p) => p.upc === upc && p.id !== editing?.id);
    if (dupUpc) return toast.error(`UPC "${upc}" is already used by "${dupUpc.name}".`);
    setSubmitting(true);
    try {
      const payload = { ...form, sku, upc, cost_price: parseFloat(form.cost_price), retail_price: parseFloat(form.retail_price) };
      // If the variant toggle is off, clear all variant linkage fields
      if (!isVariantToggle) {
        payload.variant_id = "";
        payload.is_variant_of_product_id = "";
        // Keep existing variant_group_id only if explicitly editing; otherwise clear
        if (!editing) payload.variant_group_id = "";
      }
      // If subcategory doesn't belong to current category, drop it
      if (payload.subcategory_id) {
        const sc = subcategories.find((s) => s.id === payload.subcategory_id);
        if (!sc || sc.category_id !== payload.category_id) payload.subcategory_id = "";
      }
      if (editing) { await updateInventoryProduct(editing.id, payload); toast.success("Product updated"); }
      else { await createInventoryProduct(payload); toast.success("Product created"); }
      setDialog(false); fetch();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      if (e?.response?.status === 409 && msg) toast.error(msg);
      else toast.error(msg || "Error saving product");
    }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try { await deleteInventoryProduct(id); toast.success("Deleted"); fetch(); } catch (e) { toast.error("Error"); }
  };

  const filtered = items.filter((p) => !search || p.name.toLowerCase().includes(search.toLowerCase()) || (p.sku || "").toLowerCase().includes(search.toLowerCase()));

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-teal" /></div>;

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search products..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="product-search" />
        </div>
        <Button onClick={() => openDialog()} size="sm" className="bg-teal hover:bg-teal/90" data-testid="add-product-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Product
        </Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="products-table">
          <thead>
            <tr className="border-b border-slate-200 text-left">
              <th className="py-2 px-3 font-medium text-slate-500 w-14">Image</th>
              <th className="py-2 px-3 font-medium text-slate-500">Name</th>
              <th className="py-2 px-3 font-medium text-slate-500">SKU</th>
              <th className="py-2 px-3 font-medium text-slate-500">Brand</th>
              <th className="py-2 px-3 font-medium text-slate-500">Variant</th>
              <th className="py-2 px-3 font-medium text-slate-500">Category</th>
              <th className="py-2 px-3 font-medium text-slate-500">Carrier</th>
              <th className="py-2 px-3 font-medium text-slate-500 text-right">Cost</th>
              <th className="py-2 px-3 font-medium text-slate-500 text-right">Retail</th>
              <th className="py-2 px-3 font-medium text-slate-500 text-center">Stock</th>
              <th className="py-2 px-3 font-medium text-slate-500 text-center">Serials</th>
              <th className="py-2 px-3 font-medium text-slate-500"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => {
              const variant = variantMap[p.variant_id];
              return (
              <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="py-2 px-3">
                  {p.image_url ? (
                    <img src={p.image_url} alt={p.name} className="w-10 h-10 object-cover rounded border border-slate-200" data-testid={`product-thumb-${p.id}`} />
                  ) : (
                    <div className="w-10 h-10 rounded bg-slate-100 flex items-center justify-center"><ImageIcon className="w-4 h-4 text-slate-300" /></div>
                  )}
                </td>
                <td className="py-2 px-3 font-medium text-navy">{p.name}</td>
                <td className="py-2 px-3 text-slate-600">{p.sku || "-"}</td>
                <td className="py-2 px-3 text-slate-600">{brandMap[p.brand_id] || "-"}</td>
                <td className="py-2 px-3 text-slate-600">
                  {variant ? (
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-3.5 h-3.5 rounded-full border border-slate-200" style={{ backgroundColor: variant.color_hex || "#cbd5e1" }} />
                      {variant.name}
                    </span>
                  ) : "-"}
                </td>
                <td className="py-2 px-3 text-slate-600">{catMap[p.category_id] || "-"}</td>
                <td className="py-2 px-3 text-slate-600">{p.carrier_lock || "-"}</td>
                <td className="py-2 px-3 text-right">${p.cost_price?.toFixed(2)}</td>
                <td className="py-2 px-3 text-right">${p.retail_price?.toFixed(2)}</td>
                <td className="py-2 px-3 text-center">
                  <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${p.quantity_in_stock > 0 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                    {p.quantity_in_stock || 0}
                  </span>
                </td>
                <td className="py-2 px-3 text-center">{p.track_serials ? <Check className="w-4 h-4 text-teal mx-auto" /> : <X className="w-4 h-4 text-slate-300 mx-auto" />}</td>
                <td className="py-2 px-3">
                  <div className="flex gap-1 justify-end">
                    <Button variant="ghost" size="icon" onClick={() => openDialog(p)} title="Edit" data-testid={`edit-product-${p.id}`}><Edit className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => duplicateProduct(p)} title="Duplicate" data-testid={`duplicate-product-${p.id}`}><Copy className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(p.id)} className="text-red-500" title="Delete" data-testid={`delete-product-${p.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && <p className="text-center text-slate-400 py-8">No products found</p>}
      </div>
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-5xl w-[95vw]">
          <DialogHeader><DialogTitle className="text-xl">{editing ? "Edit" : "Add"} Product</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-4 max-h-[75vh] overflow-y-auto pr-2">
            <div className="lg:col-span-2"><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-name-input" /></div>
            <div>
              <Label>SKU *</Label>
              <div className="flex gap-2">
                <Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="product-sku-input" placeholder="Unique product code" />
                <Button type="button" variant="outline" size="sm" onClick={handleGenerateSku} disabled={generatingSku} data-testid="generate-sku-btn">
                  {generatingSku ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Wand2 className="w-4 h-4 mr-1" />Generate</>}
                </Button>
              </div>
            </div>
            <div>
              <Label>UPC *</Label>
              <div className="flex gap-2">
                <Input value={form.upc} onChange={(e) => setForm({ ...form, upc: e.target.value })} data-testid="product-upc-input" placeholder="12-digit barcode" />
                <Button type="button" variant="outline" size="sm" onClick={handleGenerateUpc} disabled={generatingUpc} data-testid="generate-upc-btn">
                  {generatingUpc ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Wand2 className="w-4 h-4 mr-1" />Generate</>}
                </Button>
              </div>
            </div>
            <div>
              <Label>Category *</Label>
              <Select value={form.category_id || undefined} onValueChange={(v) => setForm({ ...form, category_id: v, subcategory_id: "" })}>
                <SelectTrigger data-testid="product-cat-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Sub-Category *</Label>
              <Select
                value={form.subcategory_id || undefined}
                onValueChange={(v) => setForm({ ...form, subcategory_id: v })}
                disabled={!form.category_id || filteredSubcats.length === 0}
              >
                <SelectTrigger data-testid="product-subcat-select">
                  <SelectValue placeholder={!form.category_id ? "Select category first" : filteredSubcats.length === 0 ? "None available" : "Select..."} />
                </SelectTrigger>
                <SelectContent>
                  {filteredSubcats.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Item Group *</Label>
              <Select value={form.item_group_id || undefined} onValueChange={(v) => setForm({ ...form, item_group_id: v })}>
                <SelectTrigger data-testid="product-itemgroup-select">
                  <SelectValue placeholder={itemGroups.length === 0 ? "None — add one first" : "Select..."} />
                </SelectTrigger>
                <SelectContent>
                  {itemGroups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Brand</Label>
              <Select value={form.brand_id || undefined} onValueChange={(v) => setForm({ ...form, brand_id: v })}>
                <SelectTrigger data-testid="product-brand-select">
                  <SelectValue placeholder={brands.length === 0 ? "No brands — add in Categories → Brands" : "Select..."} />
                </SelectTrigger>
                <SelectContent>
                  {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Warranty Period</Label>
              <Input value={form.warranty_period} onChange={(e) => setForm({ ...form, warranty_period: e.target.value })} placeholder="e.g. 12 months, 1 year" data-testid="product-warranty-input" />
            </div>
            <div>
              <Label>Cost Price ($) *</Label>
              <Input type="number" step="0.01" value={form.cost_price} onChange={(e) => setForm({ ...form, cost_price: e.target.value })} data-testid="product-cost-input" />
            </div>
            <div>
              <Label>Retail Price ($) *</Label>
              <Input type="number" step="0.01" value={form.retail_price} onChange={(e) => setForm({ ...form, retail_price: e.target.value })} data-testid="product-retail-input" />
            </div>
            <div>
              <Label>Condition *</Label>
              <Select value={form.condition || undefined} onValueChange={(v) => setForm({ ...form, condition: v })}>
                <SelectTrigger data-testid="product-condition-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>
                  {conditions.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                  {conditions.length === 0 && <SelectItem value="__none" disabled>No conditions — add in Conditions tab</SelectItem>}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Carrier Lock</Label>
              <Select value={form.carrier_lock || undefined} onValueChange={(v) => setForm({ ...form, carrier_lock: v })}>
                <SelectTrigger data-testid="product-carrier-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>
                  {carrierLocks.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                  {carrierLocks.length === 0 && <SelectItem value="__none" disabled>No options — add in Carrier Locks tab</SelectItem>}
                </SelectContent>
              </Select>
            </div>
            <div data-testid="variant-section">
              <Label>Is this a variant of another product?</Label>
              <div className="flex items-center gap-3 h-10 px-3 border border-slate-200 rounded-md bg-white">
                <Switch
                  checked={isVariantToggle}
                  onCheckedChange={(v) => {
                    setIsVariantToggle(v);
                    if (!v) {
                      setForm((f) => ({ ...f, variant_id: "", is_variant_of_product_id: "" }));
                    }
                  }}
                  data-testid="variant-toggle"
                />
                <span className="text-sm text-slate-600">{isVariantToggle ? "Yes — pick parent + variant below" : "No"}</span>
              </div>
            </div>

            {isVariantToggle && (
              <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border border-slate-200 rounded-lg bg-slate-50/50" data-testid="variant-fields">
                <div>
                  <Label>Related Product *</Label>
                  <p className="text-xs text-slate-500 mb-1">Pick a sibling product. They will share a variant group.</p>
                  <Select
                    value={form.is_variant_of_product_id || undefined}
                    onValueChange={(v) => setForm({ ...form, is_variant_of_product_id: v })}
                  >
                    <SelectTrigger data-testid="variant-related-select">
                      <SelectValue placeholder={items.length === 0 ? "No products yet" : "Pick the parent product..."} />
                    </SelectTrigger>
                    <SelectContent>
                      {items
                        .filter((p) => p.id !== editing?.id)
                        .map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.name} {p.sku ? `(${p.sku})` : ""}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Variant *</Label>
                  <p className="text-xs text-slate-500 mb-1">e.g. Black, Blue, Silver — manage in Categories → Variants.</p>
                  <Select
                    value={form.variant_id || undefined}
                    onValueChange={(v) => setForm({ ...form, variant_id: v })}
                  >
                    <SelectTrigger data-testid="variant-value-select">
                      <SelectValue placeholder={variants.length === 0 ? "No variants — add some first" : "Select..."} />
                    </SelectTrigger>
                    <SelectContent>
                      {variants.map((v) => (
                        <SelectItem key={v.id} value={v.id}>
                          <span className="inline-flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full border border-slate-200 inline-block" style={{ backgroundColor: v.color_hex || "#cbd5e1" }} />
                            {v.name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <div className="lg:col-span-2"><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>

            <div className="lg:col-span-2">
              <Label>Compatible Models</Label>
              <p className="text-xs text-slate-500 mb-2">Select all models this product is compatible with. (Phone cases, chargers, etc.)</p>
              {compatibleModels.length === 0 ? (
                <p className="text-xs text-slate-400 italic px-3 py-2 border border-dashed border-slate-200 rounded">
                  No compatible models — add some in Categories → Compatible Models.
                </p>
              ) : (
                <div className="relative" data-testid="compat-dropdown">
                  {form.compatible_model_ids.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2" data-testid="compat-selected">
                      {form.compatible_model_ids.map((id) => {
                        const m = compatibleModels.find((cm) => cm.id === id);
                        if (!m) return null;
                        return (
                          <span key={id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal/10 text-teal text-xs rounded-md">
                            {m.name}
                            <button
                              type="button"
                              onClick={() => toggleCompatibleModel(id)}
                              className="hover:text-teal-dark"
                              data-testid={`compat-chip-remove-${id}`}
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => setCompatOpen((o) => !o)}
                    className="w-full flex items-center justify-between px-3 py-2 border border-slate-200 rounded-md text-sm bg-white hover:border-teal/50 transition-colors"
                    data-testid="compat-toggle-btn"
                  >
                    <span className="text-slate-500">
                      {form.compatible_model_ids.length > 0
                        ? `${form.compatible_model_ids.length} selected — click to ${compatOpen ? "close" : "edit"}`
                        : "Click to select compatible models..."}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${compatOpen ? "rotate-180" : ""}`} />
                  </button>
                  {compatOpen && (
                    <div className="mt-1 border border-slate-200 rounded-md bg-white shadow-sm">
                      <div className="p-2 border-b border-slate-100 sticky top-0 bg-white">
                        <div className="relative">
                          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <input
                            type="text"
                            placeholder="Search models or brand..."
                            value={compatSearch}
                            onChange={(e) => setCompatSearch(e.target.value)}
                            className="w-full pl-8 pr-2 py-1.5 border border-slate-200 rounded text-sm focus:outline-none focus:border-teal"
                            data-testid="compat-search-input"
                            autoFocus
                          />
                        </div>
                      </div>
                      <div className="max-h-48 overflow-y-auto" data-testid="compat-options-list">
                        {filteredCompatModels.length === 0 && (
                          <p className="text-center text-slate-400 text-xs py-3">No matches.</p>
                        )}
                        {filteredCompatModels.map((m) => {
                          const selected = form.compatible_model_ids.includes(m.id);
                          return (
                            <label
                              key={m.id}
                              className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-sm hover:bg-slate-50 ${selected ? "bg-teal/5" : ""}`}
                              data-testid={`compat-option-${m.id}`}
                            >
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={() => toggleCompatibleModel(m.id)}
                                className="w-4 h-4 accent-teal"
                              />
                              <span className="flex-1 text-navy">
                                {m.name}
                                {m.brand && <span className="text-slate-400 text-xs"> · {m.brand}</span>}
                              </span>
                              {selected && <Check className="w-4 h-4 text-teal" />}
                            </label>
                          );
                        })}
                      </div>
                      <div className="px-3 py-2 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                        <span>{filteredCompatModels.length} of {compatibleModels.length} shown</span>
                        <button
                          type="button"
                          onClick={() => setCompatOpen(false)}
                          className="text-teal hover:underline"
                          data-testid="compat-close-btn"
                        >
                          Done
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="lg:col-span-2">
              <Label>Product Image</Label>
              <p className="text-xs text-slate-500 mb-2">Visible to dealers in the marketplace. PNG/JPG, max 5MB.</p>
              <div className="flex items-start gap-3">
                <div className="w-32 h-32 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {form.image_url ? (
                    <img src={form.image_url} alt="Product" className="w-full h-full object-cover" data-testid="product-image-preview" />
                  ) : (
                    <ImageIcon className="w-10 h-10 text-slate-300" />
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <input
                    type="file"
                    id="product-image-input"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageUpload}
                    data-testid="product-image-file"
                  />
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={uploadingImage}
                      onClick={() => document.getElementById("product-image-input")?.click()}
                      data-testid="product-image-upload-btn"
                    >
                      {uploadingImage ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
                      {form.image_url ? "Replace" : "Upload"}
                    </Button>
                    {form.image_url && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-red-500"
                        onClick={() => setForm({ ...form, image_url: "" })}
                        data-testid="product-image-remove-btn"
                      >
                        <X className="w-4 h-4 mr-1" /> Remove
                      </Button>
                    )}
                  </div>
                  <Input
                    placeholder="Or paste an image URL..."
                    value={form.image_url}
                    onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                    className="text-xs"
                    data-testid="product-image-url-input"
                  />
                </div>
              </div>
            </div>
            <div className="lg:col-span-2 flex items-center gap-3">
              <Switch checked={form.track_serials} onCheckedChange={(v) => setForm({ ...form, track_serials: v })} data-testid="product-serial-toggle" />
              <Label className="cursor-pointer">Track Serial Numbers</Label>
            </div>
            <div className="lg:col-span-2">
              <Button onClick={handleSave} disabled={submitting} className="w-full bg-teal hover:bg-teal/90 h-11" data-testid="save-product-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Product"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ========== PURCHASE ORDERS TAB ==========
function PurchaseOrdersTab() {
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ vendor_id: "", notes: "", items: [] });
  const [lineItem, setLineItem] = useState({ product_id: "", quantity: "1", unit_cost: "" });
  const [statusFilter, setStatusFilter] = useState("all");

  const fetch = useCallback(async () => {
    try {
      const [pos, vends, prods] = await Promise.all([getPurchaseOrders(), getInventoryVendors(), getInventoryProducts()]);
      setItems(pos); setVendors(vends); setProducts(prods);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const prodMap = Object.fromEntries(products.map((p) => [p.id, p]));
  const vendMap = Object.fromEntries(vendors.map((v) => [v.id, v.name]));

  const addLine = () => {
    const prod = prodMap[lineItem.product_id];
    if (!prod) return toast.error("Select a product");
    setForm({
      ...form,
      items: [...form.items, { product_id: lineItem.product_id, product_name: prod.name, quantity: parseInt(lineItem.quantity) || 1, unit_cost: parseFloat(lineItem.unit_cost) || prod.cost_price }],
    });
    setLineItem({ product_id: "", quantity: "1", unit_cost: "" });
  };

  const removeLine = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });

  const handleCreate = async () => {
    if (!form.vendor_id || form.items.length === 0) return toast.error("Select vendor and add items");
    setSubmitting(true);
    try {
      const total = form.items.reduce((s, i) => s + i.quantity * i.unit_cost, 0);
      await createPurchaseOrder({ ...form, vendor_name: vendMap[form.vendor_id] || "", total_cost: total });
      toast.success("PO created"); setDialog(false); fetch();
    } catch (e) { toast.error("Error creating PO"); }
    finally { setSubmitting(false); }
  };

  const updateStatus = async (po, newStatus) => {
    try { await updatePurchaseOrder(po.id, { status: newStatus }); toast.success("Status updated"); fetch(); }
    catch (e) { toast.error("Error"); }
  };

  const handleDeletePO = async (id) => {
    if (!window.confirm("Delete this PO?")) return;
    try { await deletePurchaseOrder(id); toast.success("Deleted"); fetch(); } catch (e) { toast.error("Error"); }
  };

  const filtered = statusFilter === "all" ? items : items.filter((po) => po.status === statusFilter);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-teal" /></div>;

  const statusColors = { draft: "bg-slate-100 text-slate-600", submitted: "bg-blue-100 text-blue-700", partially_received: "bg-amber-100 text-amber-700", received: "bg-emerald-100 text-emerald-700", cancelled: "bg-red-100 text-red-700" };

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48" data-testid="po-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="submitted">Submitted</SelectItem>
            <SelectItem value="partially_received">Partial</SelectItem>
            <SelectItem value="received">Received</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={() => { setForm({ vendor_id: "", notes: "", items: [] }); setDialog(true); }} size="sm" className="bg-teal hover:bg-teal/90" data-testid="create-po-btn">
          <Plus className="w-4 h-4 mr-1" /> Create PO
        </Button>
      </div>
      <div className="space-y-3">
        {filtered.map((po) => (
          <Card key={po.id} className="border-slate-200">
            <CardContent className="py-3 px-4">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-medium text-navy">{po.po_number}</p>
                  <p className="text-xs text-slate-500">{po.vendor_name || vendMap[po.vendor_id] || "Unknown"} | {po.items?.length || 0} items | ${po.total_cost?.toFixed(2)}</p>
                  <p className="text-xs text-slate-400 mt-1">Created {new Date(po.created_at).toLocaleDateString()} by {po.created_by}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColors[po.status] || "bg-slate-100"}`}>{po.status?.replace("_", " ")}</span>
                  {po.status === "draft" && (
                    <Button size="sm" variant="outline" onClick={() => updateStatus(po, "submitted")}>Submit</Button>
                  )}
                  {po.status === "draft" && (
                    <Button size="icon" variant="ghost" onClick={() => handleDeletePO(po.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {filtered.length === 0 && <p className="text-center text-slate-400 py-8">No purchase orders</p>}
      </div>
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-5xl w-[95vw] sm:w-[90vw]">
          <DialogHeader><DialogTitle>Create Purchase Order</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[80vh] overflow-y-auto pr-2">
            <div>
              <Label>Vendor *</Label>
              <Select value={form.vendor_id} onValueChange={(v) => setForm({ ...form, vendor_id: v })}>
                <SelectTrigger data-testid="po-vendor-select"><SelectValue placeholder="Select vendor..." /></SelectTrigger>
                <SelectContent>{vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional notes about this PO" /></div>
            <div className="border-t pt-4">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Add Line Item</Label>
              <div className="grid grid-cols-12 gap-3 mt-2 items-end">
                <div className="col-span-12 md:col-span-7">
                  <Label className="text-xs text-slate-500">Product</Label>
                  <Select value={lineItem.product_id} onValueChange={(v) => { const p = prodMap[v]; setLineItem({ ...lineItem, product_id: v, unit_cost: p?.cost_price?.toString() || "" }); }}>
                    <SelectTrigger data-testid="po-product-select"><SelectValue placeholder="Select product..." /></SelectTrigger>
                    <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}{p.sku ? ` (${p.sku})` : ""}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-4 md:col-span-2">
                  <Label className="text-xs text-slate-500">Qty</Label>
                  <Input type="number" placeholder="0" value={lineItem.quantity} onChange={(e) => setLineItem({ ...lineItem, quantity: e.target.value })} data-testid="po-qty-input" />
                </div>
                <div className="col-span-4 md:col-span-2">
                  <Label className="text-xs text-slate-500">Unit Cost ($)</Label>
                  <Input type="number" step="0.01" placeholder="0.00" value={lineItem.unit_cost} onChange={(e) => setLineItem({ ...lineItem, unit_cost: e.target.value })} data-testid="po-cost-input" />
                </div>
                <div className="col-span-4 md:col-span-1">
                  <Button size="sm" variant="outline" onClick={addLine} className="w-full" data-testid="po-add-line-btn">
                    <Plus className="w-4 h-4 mr-1" />Add
                  </Button>
                </div>
              </div>
              {form.items.length > 0 && (
                <div className="mt-4 border border-slate-200 rounded-lg overflow-hidden" data-testid="po-line-items">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr className="text-left">
                        <th className="px-3 py-2 font-medium text-slate-500">Product</th>
                        <th className="px-3 py-2 font-medium text-slate-500 text-center w-20">Qty</th>
                        <th className="px-3 py-2 font-medium text-slate-500 text-right w-28">Unit Cost</th>
                        <th className="px-3 py-2 font-medium text-slate-500 text-right w-28">Subtotal</th>
                        <th className="px-3 py-2 w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.items.map((li, i) => (
                        <tr key={i} className="border-t border-slate-100">
                          <td className="px-3 py-2 text-navy">{li.product_name}</td>
                          <td className="px-3 py-2 text-center">{li.quantity}</td>
                          <td className="px-3 py-2 text-right">${li.unit_cost.toFixed(2)}</td>
                          <td className="px-3 py-2 text-right font-medium">${(li.quantity * li.unit_cost).toFixed(2)}</td>
                          <td className="px-3 py-2">
                            <button onClick={() => removeLine(i)} className="text-red-400 hover:text-red-600" data-testid={`po-remove-line-${i}`}>
                              <X className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-slate-50 border-t border-slate-200">
                        <td colSpan={3} className="px-3 py-2 text-right font-medium text-slate-500 uppercase text-xs tracking-wide">Total</td>
                        <td className="px-3 py-2 text-right font-bold text-navy">
                          ${form.items.reduce((s, i) => s + i.quantity * i.unit_cost, 0).toFixed(2)}
                        </td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <Button onClick={handleCreate} disabled={submitting} className="w-full bg-teal hover:bg-teal/90" data-testid="save-po-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create PO"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ========== RECEIVE TAB ==========
function ReceiveTab() {
  const [pos, setPos] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPO, setSelectedPO] = useState(null);
  const [receiveItems, setReceiveItems] = useState([]);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetch = useCallback(async () => {
    try {
      const [p, pr] = await Promise.all([getPurchaseOrders(), getInventoryProducts()]);
      setPos(p.filter((po) => ["submitted", "partially_received"].includes(po.status)));
      setProducts(pr);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const prodMap = Object.fromEntries(products.map((p) => [p.id, p]));

  const selectPO = (po) => {
    setSelectedPO(po);
    setReceiveItems(po.items.map((item) => ({
      product_id: item.product_id,
      product_name: item.product_name,
      ordered_qty: item.quantity,
      quantity_received: item.quantity,
      serial_numbers: [],
      serialInput: "",
      track_serials: prodMap[item.product_id]?.track_serials || false,
    })));
  };

  const updateReceiveItem = (idx, field, value) => {
    const updated = [...receiveItems];
    updated[idx][field] = value;
    setReceiveItems(updated);
  };

  const addSerial = (idx) => {
    const item = receiveItems[idx];
    if (!item.serialInput.trim()) return;
    if (item.serial_numbers.length >= item.quantity_received) return toast.error("Already enough serials");
    const updated = [...receiveItems];
    updated[idx].serial_numbers = [...updated[idx].serial_numbers, updated[idx].serialInput.trim()];
    updated[idx].serialInput = "";
    setReceiveItems(updated);
  };

  const removeSerial = (idx, sIdx) => {
    const updated = [...receiveItems];
    updated[idx].serial_numbers = updated[idx].serial_numbers.filter((_, i) => i !== sIdx);
    setReceiveItems(updated);
  };

  const handleReceive = async () => {
    for (const item of receiveItems) {
      if (item.track_serials && item.serial_numbers.length !== item.quantity_received) {
        return toast.error(`${item.product_name}: Need ${item.quantity_received} serial numbers, got ${item.serial_numbers.length}`);
      }
    }
    setSubmitting(true);
    try {
      await receiveOrder({
        purchase_order_id: selectedPO.id,
        items: receiveItems.map((i) => ({ product_id: i.product_id, quantity_received: parseInt(i.quantity_received), serial_numbers: i.serial_numbers })),
        notes,
      });
      toast.success("Order received!"); setSelectedPO(null); fetch();
    } catch (e) { toast.error("Error receiving order"); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-teal" /></div>;

  if (selectedPO) {
    return (
      <div>
        <Button variant="ghost" onClick={() => setSelectedPO(null)} className="mb-4">&larr; Back to POs</Button>
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Receive: {selectedPO.po_number}</CardTitle>
            <p className="text-sm text-slate-500">{selectedPO.vendor_name}</p>
          </CardHeader>
          <CardContent className="space-y-4">
            {receiveItems.map((item, idx) => (
              <div key={idx} className="border border-slate-200 rounded-lg p-4">
                <div className="flex justify-between items-center mb-2">
                  <p className="font-medium text-navy">{item.product_name}</p>
                  <span className="text-xs text-slate-500">Ordered: {item.ordered_qty}</span>
                </div>
                <div className="flex items-center gap-3 mb-2">
                  <Label className="text-sm whitespace-nowrap">Qty Received:</Label>
                  <Input type="number" className="w-24" min="0" max={item.ordered_qty} value={item.quantity_received}
                    onChange={(e) => updateReceiveItem(idx, "quantity_received", parseInt(e.target.value) || 0)}
                    data-testid={`receive-qty-${idx}`} />
                </div>
                {item.track_serials && (
                  <div className="mt-2 bg-slate-50 rounded p-3">
                    <p className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">Serial Numbers ({item.serial_numbers.length}/{item.quantity_received})</p>
                    <div className="flex gap-2 mb-2">
                      <Input placeholder="Enter serial..." value={item.serialInput} className="flex-1"
                        onChange={(e) => updateReceiveItem(idx, "serialInput", e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && addSerial(idx)}
                        data-testid={`serial-input-${idx}`} />
                      <Button size="sm" variant="outline" onClick={() => addSerial(idx)} data-testid={`add-serial-${idx}`}>Add</Button>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {item.serial_numbers.map((sn, sIdx) => (
                        <span key={sIdx} className="inline-flex items-center gap-1 bg-white border px-2 py-0.5 rounded text-xs">
                          {sn} <button onClick={() => removeSerial(idx, sIdx)} className="text-red-400 hover:text-red-600"><X className="w-3 h-3" /></button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
            <div><Label>Notes</Label><Input value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
            <Button onClick={handleReceive} disabled={submitting} className="w-full bg-teal hover:bg-teal/90" data-testid="confirm-receive-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm Receive"}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <p className="text-sm text-slate-500 mb-4">Select a Purchase Order to receive</p>
      <div className="space-y-3">
        {pos.map((po) => (
          <Card key={po.id} className="border-slate-200 cursor-pointer hover:border-teal transition-colors" onClick={() => selectPO(po)} data-testid={`receive-po-${po.id}`}>
            <CardContent className="py-3 px-4 flex justify-between items-center">
              <div>
                <p className="font-medium text-navy">{po.po_number}</p>
                <p className="text-xs text-slate-500">{po.vendor_name} | {po.items?.length} items</p>
              </div>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${po.status === "submitted" ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"}`}>{po.status?.replace("_", " ")}</span>
            </CardContent>
          </Card>
        ))}
        {pos.length === 0 && <p className="text-center text-slate-400 py-8">No POs ready to receive</p>}
      </div>
    </div>
  );
}

// ========== HISTORY TAB ==========
function HistoryTab() {
  const { user } = useAuth();
  const canDelete = user?.role === "admin" || (`${user?.first_name || ""} ${user?.last_name || ""}`.trim().toLowerCase() === "ray kalasho");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const fetch = useCallback(async () => {
    try { setItems(await getReceiveHistory()); } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const handleDelete = async (e, r) => {
    e.stopPropagation();
    if (!window.confirm(`Delete this receive history record?\n\nNote: this only removes the audit row — it does NOT roll back stock. Use Corrections to adjust on-hand if needed.`)) return;
    setDeletingId(r.id);
    try {
      await deleteReceiveHistory(r.id);
      toast.success("Receive history deleted");
      if (selected?.id === r.id) setSelected(null);
      fetch();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete");
    } finally { setDeletingId(null); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-teal" /></div>;

  if (selected) {
    const totalCost = selected.items?.reduce((s, i) => s + (i.quantity_received || 0) * (i.cost_price || 0), 0) || 0;
    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <Button variant="ghost" onClick={() => setSelected(null)} data-testid="history-back-btn">&larr; Back to History</Button>
          {canDelete && (
            <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" disabled={deletingId === selected.id} onClick={(e) => handleDelete(e, selected)} data-testid="history-detail-delete">
              {deletingId === selected.id ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
              Delete
            </Button>
          )}
        </div>
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-lg">Receive Order Details</CardTitle>
                <p className="text-sm text-slate-500 mt-1">
                  {selected.po_number && <>PO: <span className="font-medium text-navy">{selected.po_number}</span> | </>}
                  {selected.vendor_name && <>Vendor: <span className="font-medium text-navy">{selected.vendor_name}</span> | </>}
                  Received by: <span className="font-medium text-navy">{selected.received_by}</span>
                </p>
                <p className="text-xs text-slate-400 mt-1">{new Date(selected.received_at).toLocaleString()}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Total Cost</p>
                <p className="text-xl font-bold text-navy">${totalCost.toFixed(2)}</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {selected.items?.map((item, idx) => (
              <div key={idx} className="border border-slate-200 rounded-lg p-4" data-testid={`history-item-${idx}`}>
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="font-medium text-navy text-base">{item.product_name}</p>
                    {item.sku && <p className="text-xs text-slate-500">SKU: {item.sku}</p>}
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-navy">Qty: {item.quantity_received}</p>
                    <p className="text-sm text-slate-500">${item.cost_price?.toFixed(2)} each</p>
                    <p className="text-sm font-medium text-navy">${((item.quantity_received || 0) * (item.cost_price || 0)).toFixed(2)} total</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3 mt-2">
                  {item.condition && (
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700 capitalize">Condition: {item.condition.replace("_", " ")}</span>
                  )}
                  {item.carrier_lock && (
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">Carrier: {item.carrier_lock}</span>
                  )}
                  {item.warranty_period && (
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-50 text-purple-700">Warranty: {item.warranty_period}</span>
                  )}
                </div>
                {item.serial_numbers?.length > 0 && (
                  <div className="mt-3 bg-slate-50 rounded p-3">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Serial Numbers ({item.serial_numbers.length})</p>
                    <div className="flex flex-wrap gap-1.5">
                      {item.serial_numbers.map((sn, sIdx) => (
                        <span key={sIdx} className="inline-block bg-white border border-slate-200 px-2.5 py-1 rounded text-xs font-mono text-navy">{sn}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
            {selected.notes && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs font-semibold text-amber-600 uppercase tracking-wide mb-1">Notes</p>
                <p className="text-sm text-amber-800">{selected.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <p className="text-sm text-slate-500 mb-4">{items.length} receive orders — click to view details</p>
      <div className="space-y-3">
        {items.map((r) => {
          const itemCount = r.items?.reduce((s, i) => s + (i.quantity_received || 0), 0) || 0;
          const totalCost = r.items?.reduce((s, i) => s + (i.quantity_received || 0) * (i.cost_price || 0), 0) || 0;
          return (
            <Card key={r.id} className="border-slate-200 cursor-pointer hover:border-teal transition-colors" onClick={() => setSelected(r)} data-testid={`history-card-${r.id}`}>
              <CardContent className="py-3 px-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium text-navy">
                      {r.po_number || "Manual Receive"}{r.vendor_name && <span className="text-slate-500 font-normal"> — {r.vendor_name}</span>}
                    </p>
                    <p className="text-xs text-slate-500">
                      {new Date(r.received_at).toLocaleDateString()} | By {r.received_by} | {r.items?.length || 0} products, {itemCount} units
                    </p>
                  </div>
                  <div className="text-right flex items-center gap-3">
                    <div>
                      <p className="font-bold text-navy">${totalCost.toFixed(2)}</p>
                      <p className="text-xs text-slate-400">total cost</p>
                    </div>
                    {canDelete && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-500 hover:bg-red-50 hover:text-red-600"
                        disabled={deletingId === r.id}
                        onClick={(e) => handleDelete(e, r)}
                        data-testid={`history-delete-${r.id}`}
                      >
                        {deletingId === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
        {items.length === 0 && <p className="text-center text-slate-400 py-8">No receive history yet</p>}
      </div>
    </div>
  );
}

// ========== CORRECTIONS TAB ==========
function CorrectionsTab() {
  const { user } = useAuth();
  const canDelete = user?.role === "admin" || (`${user?.first_name || ""} ${user?.last_name || ""}`.trim().toLowerCase() === "ray kalasho");
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ product_id: "", type: "add", quantity: "1", reason: "", serial_numbers: [] });
  const [serialInput, setSerialInput] = useState("");
  const [selected, setSelected] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const fetch = useCallback(async () => {
    try {
      const [corr, prods] = await Promise.all([getInventoryCorrections(), getInventoryProducts()]);
      setItems(corr); setProducts(prods);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const prodMap = Object.fromEntries(products.map((p) => [p.id, p]));

  const addSerial = () => {
    if (!serialInput.trim()) return;
    setForm({ ...form, serial_numbers: [...form.serial_numbers, serialInput.trim()] });
    setSerialInput("");
  };

  const handleSave = async () => {
    const prod = prodMap[form.product_id];
    if (!prod) return toast.error("Select a product");
    if (prod.track_serials && form.serial_numbers.length !== parseInt(form.quantity)) {
      return toast.error(`Need ${form.quantity} serial numbers`);
    }
    setSubmitting(true);
    try {
      await createInventoryCorrection({ ...form, product_name: prod.name, quantity: parseInt(form.quantity) });
      toast.success("Correction applied"); setDialog(false); fetch();
    } catch (e) { toast.error("Error"); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (e, c) => {
    if (e) e.stopPropagation();
    if (!window.confirm(`Delete this correction record?\n\nNote: this only removes the audit row — it does NOT reverse the stock adjustment that was originally applied.`)) return;
    setDeletingId(c.id);
    try {
      await deleteInventoryCorrection(c.id);
      toast.success("Correction deleted");
      if (selected?.id === c.id) setSelected(null);
      fetch();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to delete"); }
    finally { setDeletingId(null); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-teal" /></div>;

  // Detail view
  if (selected) {
    const totalCost = (selected.quantity || 0) * (selected.cost_price || 0);
    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <Button variant="ghost" onClick={() => setSelected(null)} data-testid="correction-back-btn">&larr; Back to Corrections</Button>
          {canDelete && (
            <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" disabled={deletingId === selected.id} onClick={(e) => handleDelete(e, selected)} data-testid="correction-detail-delete">
              {deletingId === selected.id ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
              Delete
            </Button>
          )}
        </div>
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-lg">Correction Details</CardTitle>
                <p className="text-sm text-slate-500 mt-1">
                  By: <span className="font-medium text-navy">{selected.created_by}</span>
                </p>
                <p className="text-xs text-slate-400 mt-1">{new Date(selected.created_at).toLocaleString()}</p>
              </div>
              <span className={`px-3 py-1 rounded text-sm font-medium ${selected.type === "add" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                {selected.type === "add" ? "Stock Added" : "Stock Removed"} ({selected.type === "add" ? "+" : "-"}{selected.quantity})
              </span>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="border border-slate-200 rounded-lg p-4">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <p className="font-medium text-navy text-base">{selected.product_name}</p>
                  {selected.sku && <p className="text-xs text-slate-500">SKU: {selected.sku}</p>}
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-500">${selected.cost_price?.toFixed(2)} per unit</p>
                  <p className="font-medium text-navy">${totalCost.toFixed(2)} total</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-3">
                {selected.condition && (
                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700 capitalize">Condition: {selected.condition.replace("_", " ")}</span>
                )}
                {selected.carrier_lock && (
                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">Carrier: {selected.carrier_lock}</span>
                )}
                {selected.warranty_period && (
                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-50 text-purple-700">Warranty: {selected.warranty_period}</span>
                )}
              </div>
            </div>
            {selected.serial_numbers?.length > 0 && (
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Serial Numbers ({selected.serial_numbers.length})</p>
                <div className="flex flex-wrap gap-1.5">
                  {selected.serial_numbers.map((sn, i) => (
                    <span key={i} className="inline-block bg-white border border-slate-200 px-2.5 py-1 rounded text-xs font-mono text-navy">{sn}</span>
                  ))}
                </div>
              </div>
            )}
            {selected.reason && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs font-semibold text-amber-600 uppercase tracking-wide mb-1">Reason</p>
                <p className="text-sm text-amber-800">{selected.reason}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // List view
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-slate-500">{items.length} corrections — click to view details</p>
        <Button onClick={() => { setForm({ product_id: "", type: "add", quantity: "1", reason: "", serial_numbers: [] }); setDialog(true); }} size="sm" className="bg-teal hover:bg-teal/90" data-testid="add-correction-btn">
          <Plus className="w-4 h-4 mr-1" /> New Correction
        </Button>
      </div>
      <div className="space-y-3">
        {items.map((c) => {
          const totalCost = (c.quantity || 0) * (c.cost_price || 0);
          return (
            <Card key={c.id} className="border-slate-200 cursor-pointer hover:border-teal transition-colors" onClick={() => setSelected(c)} data-testid={`correction-card-${c.id}`}>
              <CardContent className="py-3 px-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium text-navy">{c.product_name}</p>
                    <p className="text-xs text-slate-500">
                      {new Date(c.created_at).toLocaleDateString()} | By {c.created_by}
                      {c.reason && <> | {c.reason}</>}
                      {c.serial_numbers?.length > 0 && <> | {c.serial_numbers.length} serials</>}
                    </p>
                  </div>
                  <div className="text-right flex items-center gap-3">
                    <div>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${c.type === "add" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                        {c.type === "add" ? "+" : "-"}{c.quantity}
                      </span>
                      <p className="text-xs text-slate-400 mt-1">${totalCost.toFixed(2)}</p>
                    </div>
                    {canDelete && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-500 hover:bg-red-50 hover:text-red-600"
                        disabled={deletingId === c.id}
                        onClick={(e) => handleDelete(e, c)}
                        data-testid={`correction-delete-${c.id}`}
                      >
                        {deletingId === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
        {items.length === 0 && <p className="text-center text-slate-400 py-8">No corrections yet</p>}
      </div>
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Inventory Correction</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Product *</Label>
              <Select value={form.product_id} onValueChange={(v) => setForm({ ...form, product_id: v, serial_numbers: [] })}>
                <SelectTrigger data-testid="correction-product-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} (Stock: {p.quantity_in_stock})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="add">Add Stock</SelectItem>
                    <SelectItem value="remove">Remove Stock</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Quantity</Label><Input type="number" min="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="correction-qty-input" /></div>
            </div>
            <div><Label>Reason</Label><Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="correction-reason-input" /></div>
            {form.product_id && prodMap[form.product_id]?.track_serials && (
              <div className="bg-slate-50 rounded p-3">
                <p className="text-xs font-semibold text-slate-500 mb-2">SERIAL NUMBERS ({form.serial_numbers.length}/{form.quantity})</p>
                <div className="flex gap-2 mb-2">
                  <Input placeholder="Serial..." value={serialInput} onChange={(e) => setSerialInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addSerial()} />
                  <Button size="sm" variant="outline" onClick={addSerial}>Add</Button>
                </div>
                <div className="flex flex-wrap gap-1">
                  {form.serial_numbers.map((sn, i) => (
                    <span key={i} className="inline-flex items-center gap-1 bg-white border px-2 py-0.5 rounded text-xs">
                      {sn} <button onClick={() => setForm({ ...form, serial_numbers: form.serial_numbers.filter((_, j) => j !== i) })} className="text-red-400"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
              </div>
            )}
            <Button onClick={handleSave} disabled={submitting} className="w-full bg-teal hover:bg-teal/90" data-testid="save-correction-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply Correction"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ========== STOCK VIEWER TAB ==========
function StockTab() {
  const [rows, setRows] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  // Filter state
  const [fName, setFName] = useState("");
  const [fCategory, setFCategory] = useState("");
  const [fSubcategory, setFSubcategory] = useState("");
  const [fSupplier, setFSupplier] = useState("");
  const [fModel, setFModel] = useState("");
  const [fSku, setFSku] = useState("");
  const [fUpc, setFUpc] = useState("");
  const [fSerial, setFSerial] = useState("");
  const [activeFilters, setActiveFilters] = useState(null);
  const [sort, setSort] = useState({ key: "name", dir: "asc" });
  const [serialDialog, setSerialDialog] = useState(null); // {row}

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [data, v, m] = await Promise.all([
        getStockViewer(),
        getInventoryVendors(),
        getInventoryCompatibleModels(),
      ]);
      setRows(Array.isArray(data) ? data : []);
      setVendors(Array.isArray(v) ? v : []);
      setModels(Array.isArray(m) ? m : []);
    } catch (e) {
      console.error("Stock viewer load failed:", e);
      toast.error(e?.response?.data?.detail || `Failed to load stock viewer: ${e.message || "unknown error"}`);
    }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const allCategories = Array.from(new Map(rows.filter((r) => r.category_id).map((r) => [r.category_id, r.category_name])).entries());
  const allSubcategories = Array.from(new Map(rows.filter((r) => r.subcategory_id && (!fCategory || r.category_id === fCategory)).map((r) => [r.subcategory_id, r.subcategory_name])).entries());

  const filtered = (() => {
    const f = activeFilters;
    if (!f) return rows;
    return rows.filter((r) => {
      if (f.name && !r.name.toLowerCase().includes(f.name.toLowerCase())) return false;
      if (f.category && r.category_id !== f.category) return false;
      if (f.subcategory && r.subcategory_id !== f.subcategory) return false;
      if (f.model && !(r.compatible_model_ids || []).includes(f.model)) return false;
      if (f.sku && !(r.sku || "").toLowerCase().includes(f.sku.toLowerCase())) return false;
      if (f.upc && !(r.upc || "").toLowerCase().includes(f.upc.toLowerCase())) return false;
      if (f.serial && !(r.available_serials || []).some((s) => (s.serial_number || "").includes(f.serial))) return false;
      return true;
    });
  })();

  const sorted = [...filtered].sort((a, b) => {
    const k = sort.key;
    let av = a[k]; let bv = b[k];
    if (typeof av === "string") av = av.toLowerCase();
    if (typeof bv === "string") bv = bv.toLowerCase();
    if (av == null) av = "";
    if (bv == null) bv = "";
    if (av < bv) return sort.dir === "asc" ? -1 : 1;
    if (av > bv) return sort.dir === "asc" ? 1 : -1;
    return 0;
  });

  const toggleSort = (key) => {
    setSort((s) => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });
  };

  const handleSearch = () => {
    setActiveFilters({
      name: fName.trim(), category: fCategory, subcategory: fSubcategory,
      supplier: fSupplier, model: fModel, sku: fSku.trim(), upc: fUpc.trim(), serial: fSerial.trim(),
    });
  };
  const handleReset = () => {
    setFName(""); setFCategory(""); setFSubcategory(""); setFSupplier("");
    setFModel(""); setFSku(""); setFUpc(""); setFSerial("");
    setActiveFilters(null);
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-teal" /></div>;

  const Th = ({ k, label, className = "" }) => (
    <th className={`py-2 px-3 font-medium text-slate-500 cursor-pointer select-none whitespace-nowrap ${className}`} onClick={() => toggleSort(k)} data-testid={`stock-sort-${k}`}>
      <span className="inline-flex items-center gap-0.5">
        {label}
        <ArrowDown className={`w-3 h-3 transition-transform ${sort.key === k ? "text-teal" : "text-slate-300"} ${sort.key === k && sort.dir === "asc" ? "rotate-180" : ""}`} />
      </span>
    </th>
  );

  return (
    <div data-testid="stock-viewer">
      {/* Filter Panel */}
      <Card className="border-slate-200 mb-4">
        <CardContent className="p-4 lg:p-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="sv-name">Name</Label>
              <Input id="sv-name" value={fName} onChange={(e) => setFName(e.target.value)} placeholder="Enter name" data-testid="sv-name" />
            </div>
            <div>
              <Label>Category</Label>
              <select value={fCategory} onChange={(e) => { setFCategory(e.target.value); setFSubcategory(""); }} className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="sv-category">
                <option value="">Select category</option>
                {allCategories.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
              </select>
            </div>
            <div>
              <Label>Sub-Category</Label>
              <select value={fSubcategory} onChange={(e) => setFSubcategory(e.target.value)} className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" disabled={!fCategory} data-testid="sv-subcategory">
                <option value="">{fCategory ? "Select sub-category" : "Select category first"}</option>
                {allSubcategories.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
              </select>
            </div>
            <div>
              <Label>Supplier</Label>
              <select value={fSupplier} onChange={(e) => setFSupplier(e.target.value)} className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="sv-supplier">
                <option value="">Select supplier</option>
                {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </div>
            <div>
              <Label>Model</Label>
              <select value={fModel} onChange={(e) => setFModel(e.target.value)} className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="sv-model">
                <option value="">Select model</option>
                {models.map((m) => <option key={m.id} value={m.id}>{m.name}{m.brand ? ` (${m.brand})` : ""}</option>)}
              </select>
            </div>
            <div>
              <Label htmlFor="sv-sku">SKU</Label>
              <Input id="sv-sku" value={fSku} onChange={(e) => setFSku(e.target.value)} placeholder="Enter SKU" data-testid="sv-sku" />
            </div>
            <div>
              <Label htmlFor="sv-upc">UPC</Label>
              <Input id="sv-upc" value={fUpc} onChange={(e) => setFUpc(e.target.value)} placeholder="Enter UPC" data-testid="sv-upc" />
            </div>
            <div>
              <Label htmlFor="sv-serial">Serial</Label>
              <Input id="sv-serial" value={fSerial} onChange={(e) => setFSerial(e.target.value)} placeholder="Enter serial" data-testid="sv-serial" />
            </div>
          </div>
          <div className="flex justify-end items-center gap-2 mt-4 pt-3 border-t border-slate-100">
            <Button variant="ghost" size="sm" onClick={handleReset} data-testid="sv-reset">
              <ArrowUp className="w-4 h-4 mr-1 rotate-180" /> Reset
            </Button>
            <Button size="sm" className="bg-teal hover:bg-teal/90 text-white" onClick={handleSearch} data-testid="sv-search">
              <Search className="w-4 h-4 mr-1" /> Search
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <p className="text-sm text-slate-500">
              <span className="font-medium text-navy">{sorted.length}</span> of {rows.length} products
              {activeFilters && <button onClick={handleReset} className="ml-2 text-teal underline text-xs">clear filters</button>}
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="stock-table">
              <thead className="bg-slate-50">
                <tr className="text-left">
                  <Th k="sku" label="SKU" />
                  <Th k="upc" label="UPC" />
                  <Th k="category_name" label="Category" />
                  <Th k="subcategory_name" label="Sub-Category" />
                  <Th k="item_group_name" label="Item Group" />
                  <Th k="name" label="Name" className="min-w-[260px]" />
                  <Th k="condition" label="Condition" />
                  <Th k="on_hand" label="On-Hand" className="text-center" />
                  <Th k="pending" label="Pending" className="text-center" />
                  <th className="py-2 px-3 font-medium text-slate-500 text-center">Serial #</th>
                  <Th k="retail_price" label="Retail Price" className="text-right" />
                  <Th k="cost_price" label="Cost" className="text-right" />
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`stock-row-${r.id}`}>
                    <td className="py-2 px-3 font-mono text-slate-700 whitespace-nowrap">{r.sku || <span className="text-slate-300">—</span>}</td>
                    <td className="py-2 px-3 font-mono text-slate-700 whitespace-nowrap">{r.upc || <span className="text-slate-300">—</span>}</td>
                    <td className="py-2 px-3 text-slate-700">{r.category_name || <span className="text-slate-300">—</span>}</td>
                    <td className="py-2 px-3 text-slate-700">{r.subcategory_name || <span className="text-slate-300">—</span>}</td>
                    <td className="py-2 px-3 text-slate-700">{r.item_group_name || <span className="text-slate-300">—</span>}</td>
                    <td className="py-2 px-3 font-medium text-navy min-w-[260px]">{r.name}</td>
                    <td className="py-2 px-3">
                      {r.condition ? <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700 capitalize whitespace-nowrap">{r.condition.replace("_", " ")}</span> : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="py-2 px-3 text-center font-semibold">
                      <span className={r.on_hand > 0 ? "text-emerald-600" : "text-red-500"}>{r.on_hand}</span>
                    </td>
                    <td className="py-2 px-3 text-center font-semibold">
                      <span className={r.pending > 0 ? "text-amber-600" : "text-slate-400"}>{r.pending}</span>
                    </td>
                    <td className="py-2 px-3 text-center">
                      {(r.available_serials || []).length > 0 ? (
                        <button onClick={() => setSerialDialog(r)} className="text-teal hover:underline text-xs font-medium" data-testid={`view-serials-${r.id}`}>
                          View ({r.available_serials.length})
                        </button>
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-right font-medium text-navy whitespace-nowrap">${r.retail_price.toFixed(2)}</td>
                    <td className="py-2 px-3 text-right text-slate-600 whitespace-nowrap">${r.cost_price.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {sorted.length === 0 && (
              <p className="text-center text-slate-400 py-8">
                {rows.length === 0 ? "No products yet — add one in the Products tab" : "No products match your filters"}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* On-Hand Serials Dialog */}
      <Dialog open={!!serialDialog} onOpenChange={(o) => !o && setSerialDialog(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{serialDialog?.name} — On-Hand Serials</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 mb-2">Serials currently in stock (not sold to dealers). For full lifetime history, see the Serials tab.</p>
          <div className="max-h-96 overflow-y-auto border border-slate-200 rounded-md p-3 space-y-1 bg-slate-50" data-testid="serial-dialog-list">
            {(serialDialog?.available_serials || []).map((s) => (
              <div key={s.id} className="flex items-center justify-between font-mono text-sm bg-white rounded px-3 py-1.5 border border-slate-100">
                <span className="text-navy">{s.serial_number}</span>
                {s.condition && <span className="text-xs text-slate-500 capitalize">{s.condition}</span>}
              </div>
            ))}
            {(serialDialog?.available_serials || []).length === 0 && (
              <p className="text-slate-400 text-center py-4">No serials in stock</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ========== SERIALS TAB ==========
function SerialsTab() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const doSearch = async () => {
    if (!query.trim()) return;
    setLoading(true);
    try { setResults(await searchSerialNumbers(query)); } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const statusColors = { available: "bg-emerald-100 text-emerald-700", sold: "bg-blue-100 text-blue-700", reserved: "bg-amber-100 text-amber-700" };

  return (
    <div>
      <div className="flex gap-2 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search serial number..." value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doSearch()} className="pl-9" data-testid="serial-search-input" />
        </div>
        <Button onClick={doSearch} disabled={loading} className="bg-teal hover:bg-teal/90" data-testid="serial-search-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search"}
        </Button>
      </div>
      {results.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="serial-results-table">
            <thead>
              <tr className="border-b border-slate-200 text-left">
                <th className="py-2 px-3 font-medium text-slate-500">Serial #</th>
                <th className="py-2 px-3 font-medium text-slate-500">Product</th>
                <th className="py-2 px-3 font-medium text-slate-500">SKU</th>
                <th className="py-2 px-3 font-medium text-slate-500">Status</th>
                <th className="py-2 px-3 font-medium text-slate-500">Dealer</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r) => (
                <tr key={r.id} className="border-b border-slate-100">
                  <td className="py-2 px-3 font-mono text-navy">{r.serial_number}</td>
                  <td className="py-2 px-3">{r.product_name || r.product_id}</td>
                  <td className="py-2 px-3 text-slate-500">{r.product_sku || "-"}</td>
                  <td className="py-2 px-3"><span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColors[r.status] || "bg-slate-100"}`}>{r.status}</span></td>
                  <td className="py-2 px-3 text-slate-500">{r.dealer_name || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {results.length === 0 && query && !loading && <p className="text-center text-slate-400 py-8">No results found</p>}
      {!query && <p className="text-center text-slate-400 py-8">Enter a serial number to search</p>}
    </div>
  );
}

// ========== MAIN COMPONENT ==========
export default function AdminInventory() {
  const [activeTab, setActiveTab] = useState("products");

  const renderTab = () => {
    switch (activeTab) {
      case "products": return <ProductsTab />;
      case "categories": return <CategoriesPanel />;
      case "conditions": return <ConditionsTab />;
      case "carrier_locks": return <CarrierLocksTab />;
      case "vendors": return <VendorsTab />;
      case "purchase_orders": return <PurchaseOrdersTab />;
      case "receive": return <ReceiveTab />;
      case "history": return <HistoryTab />;
      case "corrections": return <CorrectionsTab />;
      case "stock": return <StockTab />;
      case "serials": return <SerialsTab />;
      case "orders": return <AdminMarketplaceOrders embedded />;
      default: return <ProductsTab />;
    }
  };

  return (
    <AdminLayout title="Inventory Management" breadcrumb="Inventory">
      <div className="space-y-6" data-testid="admin-inventory">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-1 bg-white border border-slate-200 rounded-lg p-1 flex-1 min-w-0" data-testid="inventory-tabs">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  data-testid={`inv-tab-${tab.key}`}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    activeTab === tab.key ? "bg-teal text-white" : "text-slate-500 hover:bg-slate-100"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
        <Card className="border-slate-200">
          <CardContent className="p-4 lg:p-6">
            {renderTab()}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
